// src/services/imagePromptGenerator.js
// AI Image Prompt Generator for Hermod's Path — Canva Magic Media & Leonardo AI

const FUNCTION_URL = process.env.NODE_ENV === 'production'
  ? 'https://us-central1-heimdl.cloudfunctions.net/enhanceImagePrompt'
  : 'http://localhost:5001/heimdl/us-central1/enhanceImagePrompt';

// ── Platform-specific prompt styles ──

const PLATFORM_STYLES = {
  'professional-ig': {
    canva: {
      prefix: 'Square 1080x1080.',
      style: 'Warm, joyful school library setting. Yellow (#FFD93D) and blue (#4389BD) colour palette. Bright, inviting, professional. Clean lines with soft natural lighting.',
      suffix: 'No text overlay. Photo-realistic style.',
    },
    leonardo: {
      prefix: 'Square format, 1080x1080 pixels.',
      style: 'Warm golden lighting, joyful atmosphere. Primary palette: sunflower yellow (#FFD93D), ocean blue (#4389BD), cream highlights. Professional school library context. Clean composition with shallow depth of field. Natural, authentic feeling.',
      suffix: 'Photorealistic rendering, soft bokeh background, warm color grading, no visible text or watermarks.',
    },
  },
  'professional-fb': {
    canva: {
      prefix: 'Square 1080x1080.',
      style: 'Warm, joyful school library setting. Yellow (#FFD93D) and blue (#4389BD) colour palette. Bright, inviting, professional. Clean lines with soft natural lighting.',
      suffix: 'No text overlay. Photo-realistic style.',
    },
    leonardo: {
      prefix: 'Square format, 1080x1080 pixels.',
      style: 'Warm golden lighting, joyful atmosphere. Primary palette: sunflower yellow (#FFD93D), ocean blue (#4389BD), cream highlights. Professional school library context. Clean composition with shallow depth of field. Natural, authentic feeling.',
      suffix: 'Photorealistic rendering, soft bokeh background, warm color grading, no visible text or watermarks.',
    },
  },
  'lux-libris-ig': {
    canva: {
      prefix: 'Square 1080x1080.',
      style: 'Light, airy Catholic aesthetic. Soft blue (#ADD4EA), cream, and gold (#D4A843) palette. Gentle, reverent but joyful. Storybook quality with soft edges.',
      suffix: 'No text overlay. Illustrated or soft-focus style.',
    },
    leonardo: {
      prefix: 'Square format, 1080x1080 pixels.',
      style: "Ethereal, luminous atmosphere with soft diffused lighting. Palette: celestial blue (#ADD4EA), warm cream (#FFFBF0), antiqued gold (#D4A843). Catholic visual tradition — think illuminated manuscripts meet modern children's illustration. Gentle, sacred, wonder-filled.",
      suffix: 'Soft watercolor-digital hybrid rendering, subtle golden light rays, no visible text, no harsh shadows, dreamlike quality.',
    },
  },
  'lux-libris-fb': {
    canva: {
      prefix: 'Square 1080x1080.',
      style: 'Light, airy Catholic aesthetic. Soft blue (#ADD4EA), cream, and gold (#D4A843) palette. Gentle, reverent but joyful. Storybook quality with soft edges.',
      suffix: 'No text overlay. Illustrated or soft-focus style.',
    },
    leonardo: {
      prefix: 'Square format, 1080x1080 pixels.',
      style: "Ethereal, luminous atmosphere with soft diffused lighting. Palette: celestial blue (#ADD4EA), warm cream (#FFFBF0), antiqued gold (#D4A843). Catholic visual tradition — think illuminated manuscripts meet modern children's illustration. Gentle, sacred, wonder-filled.",
      suffix: 'Soft watercolor-digital hybrid rendering, subtle golden light rays, no visible text, no harsh shadows, dreamlike quality.',
    },
  },
  'linkedin': {
    canva: {
      prefix: 'Landscape 1200x627.',
      style: 'Professional, clean, modern. Yellow (#FFD93D) and blue (#4389BD) palette. Corporate-friendly but warm. Minimal design, strong visual hierarchy.',
      suffix: 'No text overlay. Clean professional style.',
    },
    leonardo: {
      prefix: 'Landscape format, 1200x627 pixels.',
      style: 'Clean, professional composition with strong visual hierarchy. Primary palette: yellow (#FFD93D) accent, blue (#4389BD) base, white space. Modern professional setting — think thought leadership visual. Balanced, confident, approachable.',
      suffix: 'Sharp, clean rendering, subtle gradient backgrounds, no text, modern minimalist aesthetic, professional lighting.',
    },
  },
};

/**
 * Build a base prompt from static templates (no API call needed)
 * @param {string} imageNote - The Seer's image suggestion or user description
 * @param {'canva'|'leonardo'} tool - Target AI image tool
 * @param {string} platformId - Platform identifier
 * @returns {string} Base prompt ready to use
 */
export function buildBasePrompt(imageNote, tool, platformId) {
  const platform = PLATFORM_STYLES[platformId] || PLATFORM_STYLES['professional-ig'];
  const s = platform[tool] || platform.canva;

  if (tool === 'canva') {
    return `${s.prefix} ${imageNote}. ${s.style} ${s.suffix}`;
  }
  return `${s.prefix}\n\nSubject: ${imageNote}\n\nStyle: ${s.style}\n\nTechnical: ${s.suffix}`;
}

/**
 * Enhance a prompt via the Cloud Function (OpenAI expansion)
 * @param {string} imageNote - The Seer's image suggestion or user description
 * @param {'canva'|'leonardo'} tool - Target AI image tool
 * @param {string} platformId - Platform identifier
 * @returns {Promise<string>} Enhanced prompt
 */
export async function enhancePrompt(imageNote, tool, platformId) {
  const response = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageNote, tool, platformId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Prompt enhancement failed (${response.status})`);
  }

  const data = await response.json();
  if (!data.success || !data.prompt) {
    throw new Error('Unexpected response from prompt enhancer');
  }

  return data.prompt;
}
